import apiService from '@/services/ApiService';
import { ActionContext } from 'vuex';
import { State } from '@/store/state';
import { ConnectorConfig } from '@/models/ConnectorConfig';
import { ConnectorSyncStatus } from '@/store/state/ConnectorState';
import {
  commitSetConnectors,
  commitSetConnectorLoading,
  commitSetConnectorError,
  commitAddConnector,
  commitRemoveConnector,
  commitUpdateConnector,
  commitSetConnectorSyncState,
  commitHydrateConnectorSyncStates,
} from '@/store/commits/connectorCommits';

interface BulkSyncStatusEntry {
  status: 'in_progress' | 'error' | 'idle' | 'done';
  error?: string;
  jobId?: string | null;
  lastSyncAt: number | null;
}

export interface StartConnectorSyncResult {
  jobId: string | null;
  alreadyRunning: boolean;
  since: number;
}

export const connectorActions = {
  async loadConnectors(_context: ActionContext<State, State>, workspaceId: string) {
    commitSetConnectorLoading(true);
    commitSetConnectorError(null);
    try {
      const res = await apiService.connector.listByWorkspace(workspaceId);
      const connectors = ((res.data as any).connectors || []).map(ConnectorConfig.parse);
      commitSetConnectors(connectors);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load connectors';
      commitSetConnectorError(message);
    } finally {
      commitSetConnectorLoading(false);
    }
  },

  async registerConnector(_context: ActionContext<State, State>, payload: Record<string, unknown>) {
    const res = await apiService.connector.register(payload);
    const connector = ConnectorConfig.parse(res.data);
    commitAddConnector(connector);
    return connector;
  },

  async triggerSync(_context: ActionContext<State, State>, connectorId: string) {
    await apiService.connector.triggerSync(connectorId);
  },

  async startConnectorSync(
    _context: ActionContext<State, State>,
    connectorId: string
  ): Promise<StartConnectorSyncResult> {
    const since = Date.now();
    commitSetConnectorSyncState(connectorId, { status: 'in_progress', since });

    try {
      const res = await apiService.connector.triggerSync(connectorId);
      const data = res.data as { jobId?: string | null; alreadyRunning?: boolean };
      commitSetConnectorSyncState(connectorId, {
        status: 'in_progress',
        since,
        jobId: data.jobId ?? null,
      });
      return {
        jobId: data.jobId ?? null,
        alreadyRunning: data.alreadyRunning === true,
        since,
      };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to trigger sync';
      commitSetConnectorSyncState(connectorId, { status: 'error', error: message });
      throw err;
    }
  },

  async refreshSyncStatuses(_context: ActionContext<State, State>, connectorIds: string[]) {
    if (connectorIds.length === 0) {
      commitHydrateConnectorSyncStates({});
      return {} as Record<string, ConnectorSyncStatus>;
    }

    const res = await apiService.connector.getSyncStatuses(connectorIds);
    const statuses = (res.data as { statuses?: Record<string, BulkSyncStatusEntry> }).statuses ?? {};

    const hydrated: Record<string, ConnectorSyncStatus> = {};
    for (const [cid, entry] of Object.entries(statuses)) {
      if (entry.status === 'in_progress' || entry.status === 'error') {
        hydrated[cid] = {
          status: entry.status,
          jobId: entry.jobId ?? null,
          error: entry.error,
          // Use lastSyncAt as the `since` reference: the next sync writing a fresher
          // lastSyncAt is what flips status to 'done'. Falls back to 0 when never synced.
          since: typeof entry.lastSyncAt === 'number' ? entry.lastSyncAt : 0,
        };
      }
    }

    commitHydrateConnectorSyncStates(hydrated);
    return hydrated;
  },

  async disconnectConnector(
    _context: ActionContext<State, State>,
    { connectorId, keepArticles = false }: { connectorId: string; keepArticles?: boolean }
  ) {
    const res = await apiService.connector.disconnect(connectorId, keepArticles);
    commitRemoveConnector(connectorId);
    return res.data;
  },

  async reconnectConnector(_context: ActionContext<State, State>, payload: Record<string, unknown>) {
    const res = await apiService.connector.reconnect(payload);
    const connector = ConnectorConfig.parse((res.data as any).connector);
    commitAddConnector(connector);
    return res.data;
  },

  async updateConnector(
    _context: ActionContext<State, State>,
    { connectorId, payload }: { connectorId: string; payload: Record<string, unknown> }
  ) {
    const res = await apiService.connector.update(connectorId, payload);
    const connector = ConnectorConfig.parse(res.data);
    commitUpdateConnector(connector);
    return connector;
  },
};
